import type { ReactElement } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Flex } from "@similarweb/ui-components/foundations";

import type { ChipItem } from "app/pages/sales-intelligence/common-components/filters-panel";
import { LimitedChipItems } from "app/pages/sales-intelligence/common-components/filters-panel";
import { FilterResultContainer } from "app/pages/sales-intelligence/common-components/filters-panel/components/styles";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { globalStringSanitizePipeline } from "app/pages/sales-intelligence/services/text-transform/stringTransformPipeline";
import { translateNested } from "app/pages/sales-intelligence/utils/common";
import { commonKeys } from "app/pages/sales-intelligence/utils/i18n/common";

import { MAX_CHIPS_GROUP_AMOUNT } from "../../../constants";
import type SignalsFilter from "../../../models/SignalsFilter/SignalsFilter";

const SignalsFilterResult = ({
    filter,
    value,
    isExpanded,
    isActionEnabled = true,
    onGetLifecycleStatus,
    onFilterUpdate,
    onFilterValueRemove,
}: IFilterViewBaseProps<SignalsFilter>): ReactElement | null => {
    const { translate } = useTranslate();
    const selectedUISignals = filter.getSelectedUISignals(value);
    const chipItems: ChipItem[] = selectedUISignals.map((selected) => ({
        id: selected.id,
        text: globalStringSanitizePipeline.apply(translateNested(selected.name)),
        chipStatus: onGetLifecycleStatus?.(selected),
    }));

    if (!chipItems.length) {
        return null;
    }

    const handleOnChipClose = (signalId: string): void => {
        const nextValue = filter.removeSelectedSignal(signalId, value);
        onFilterUpdate(nextValue);
        onFilterValueRemove?.(nextValue);
    };

    return (
        <FilterResultContainer data-automation="signals-filter-result">
            <Flex
                flexWrap="wrap"
                alignItems="baseline"
            >
                <LimitedChipItems
                    items={chipItems}
                    onDelete={handleOnChipClose}
                    maxItems={MAX_CHIPS_GROUP_AMOUNT}
                    delimiter={translate(commonKeys.and).toLocaleUpperCase()}
                    hideActionButton={!isActionEnabled}
                    isTabExpanded={isExpanded}
                />
            </Flex>
        </FilterResultContainer>
    );
};

export default SignalsFilterResult;
